import { supabase } from "@/lib/supabase";
import { Tables } from "@/types/supabase-types";
import { CADCurrencyConverter } from "./currency";
import { ItemUOMSelectResult, calculateBaseQuantity, itemUOMSelect } from "./item-uoms";
import { Item, ItemSelectResult, itemSelect, processItem } from "./items";

export interface Adjustment extends Omit<AdjustmentSelectResult, "lines"> {
  allLines: AdjustmentLineItem[];
  salesOrderRackLines: AdjustmentLineItem[];
}

export interface AdjustmentLineItem extends Omit<AdjustmentLineItemSelectResult, "item"> {
  /** Quantity difference converted to the base UOM */
  baseQuantityDiff: number;
  /** New item quantity converted to the base UOM */
  baseNewQuantity: number;
  item: Item;
}

export interface AdjustmentSelectResult extends Pick<Tables<"Adjustment">, "id" | "number" | "date" | "comment"> {
  lines: AdjustmentLineItemSelectResult[];
}

export interface AdjustmentLineItemSelectResult extends Pick<Tables<"AdjustmentLineItem">, "id" | "description"> {
  lineNumber: number;
  item: ItemSelectResult;
  job: Pick<Tables<"Job">, "id" | "name"> | null;
  /** Quantity difference in the selected ItemUOM */
  uomQuantityDiff: number;
  /** New item quantity in the selected ItemUOM */
  uomNewQuantity: number;
  itemUOM: ItemUOMSelectResult | null;
  bin: Pick<Tables<"Bin">, "id" | "number"> | null;
}

const adjustmentSelect = `
id,
number,
date,
comment,
lines:AdjustmentLineItem!inner(
  id,
  lineNumber:linenumber,
  item:Item(${itemSelect}),
  job:Job(id, name),
  description,
  uomQuantityDiff:quantityDiff,
  uomNewQuantity:newQuantity,
  itemUOM:ItemUOM(${itemUOMSelect}),
  bin:Bin(id, number)
)
`;

export async function getAdjustments(binId: number, CADCurrencyConverter: CADCurrencyConverter): Promise<Adjustment[]> {
  // Get all adjustment ids with any non-deleted line item to or from the sales order rack bin
  const adjustmentIds = new Set(
    (
      await supabase
        .from("AdjustmentLineItem")
        .select("adjustmentId")
        .eq("binId", binId)
        .eq("deleted", false)
        .throwOnError()
    ).data.map((x) => x.adjustmentId),
  );

  // Get all non-deleted adjustments with the matched adjustment ids
  const queryResult = (
    await supabase
      .from("Adjustment")
      .select<typeof adjustmentSelect, AdjustmentSelectResult>(adjustmentSelect)
      .in("id", [...adjustmentIds])
      .eq("deleted", false)
      .eq("lines.deleted", false)
      .order("date", { ascending: false })
      .order("linenumber", { referencedTable: "lines", ascending: true })
      .throwOnError()
  ).data;

  // Do some additional client-side processing on the results before returning
  return queryResult.map((po) => processAdjustment(po, binId, CADCurrencyConverter));
}

function processAdjustment(
  { lines: rawLines, ...adjustment }: AdjustmentSelectResult,
  binId: number,
  CADCurrencyConverter: CADCurrencyConverter,
): Adjustment {
  const allLines = rawLines.map((line) => processAdjustmentLineItem(line, CADCurrencyConverter));
  const salesOrderRackLines = allLines.filter((x) => x.bin?.id === binId);
  return { ...adjustment, allLines, salesOrderRackLines };
}

function processAdjustmentLineItem(
  line: AdjustmentLineItemSelectResult,
  CADCurrencyConverter: CADCurrencyConverter,
): AdjustmentLineItem {
  return {
    ...line,
    baseQuantityDiff: calculateBaseQuantity(line.uomQuantityDiff, line.itemUOM),
    baseNewQuantity: calculateBaseQuantity(line.uomNewQuantity, line.itemUOM),
    item: processItem(line.item, CADCurrencyConverter),
  };
}
